// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useDialogControl } from '@hooks/other/useDialogControl';
import { useCallback, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Mint,
  MintRequest,
} from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Mint';
import { ContractId } from '@daml/types';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { RejectDialog } from '../../../components/Dialog/RejectDialog';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { StateCell, Status } from '../../../components/StateCell';
import { DataTable } from '../../../components/Table/DataTable';
import { useParties } from '../../../contexts/PartiesContext';
import { useAcceptMintRequest } from '../../../hooks/mutations/registry/mint/useAcceptMintRequest';
import { useCancelMintRequest } from '../../../hooks/mutations/registry/mint/useCancelMintRequest';
import { useRejectMintRequest } from '../../../hooks/mutations/registry/mint/useRejectMintRequest';
import useAcceptedMints from '../../../hooks/queries/registry/mint/useAcceptedMints';
import useExecutedMints from '../../../hooks/queries/registry/mint/useExecutedMints';
import useFailedMints from '../../../hooks/queries/registry/mint/useFailedMints';
import useMintOffers from '../../../hooks/queries/registry/mint/useMintOffers';
import useMintRequests from '../../../hooks/queries/registry/mint/useMintRequests';
import useRejectedMints from '../../../hooks/queries/registry/mint/useRejectedMints';
import useRegistrarService from '../../../hooks/queries/registry/onboarding/useRegistrarService';
import useRegistrarServices from '../../../hooks/queries/registry/onboarding/useRegistrarServices';
import { fmt, shorten } from '../../../utils/common';
import { ERR_ACC_MINT, ERR_REJ_MINT } from '../../../utils/strings';
import { PageWrapper } from '../../PageWrapper';
import { RequestMintDialog } from './RequestMintDialog';

type Row = {
  contractId: string;
  state: string;
  payload: Mint;
  reason?: string;
};

type Action = {
  name: string;
  action: (r: Row) => Promise<unknown>;
};

const createHeaders: (createActions: (r: Row) => Action[]) => GridColDef<Row>[] = (
  createActions,
) => {
  const renderActionCell = (params: GridRenderCellParams<Row>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ name, action }) => (
          <AsyncButton key={name} sx={{ mr: 1 }} onClick={() => action(params.row)}>
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'state',
      headerName: 'State',
      renderCell: (params) => {
        return <StateCell state={params.row.state as Status} reason={params.row.reason} />;
      },
      flex: 100,
      filterable: false,
    },
    {
      field: 'registrar',
      headerName: 'Registrar',
      renderCell: (params) => <PartyDisplay party={params.row.payload.registrar} />,
      flex: 100,
      filterable: false,
    },
    {
      field: 'holder',
      headerName: 'Holder',
      renderCell: (params) => <PartyDisplay party={params.row.payload.holder} />,
      flex: 100,
      filterable: false,
    },
    {
      field: 'instrumentId',
      headerName: 'Instrument Id',
      valueGetter: (params) => params.row.payload.instrumentIdentifier.id,
      flex: 150,
      filterable: false,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      renderCell: (params) => fmt(params.row.payload.amount, 2),
      valueGetter: (params) => Number(params.row.payload.amount),
      flex: 150,
      filterable: false,
    },
    {
      field: 'batch',
      headerName: 'Batch (size)',
      valueGetter: (params) =>
        `${shorten(params.row.payload.batch.id)} (${params.row.payload.batch.size})`,
      flex: 150,
      filterable: false,
    },
    {
      field: 'reference',
      headerName: 'Reference',
      valueGetter: (params) => params.row.payload.reference,
      flex: 150,
      filterable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      flex: 150,
      filterable: false,
    },
  ];
};

export const Mints = () => {
  const [onReject, setOnReject] = useState<((reason: string) => Promise<unknown>) | undefined>();
  const { party } = useParties();
  const registrarService = useRegistrarService();
  const registrarServices = useRegistrarServices();
  const mintRequests = useMintRequests();
  const mintOffers = useMintOffers();
  const acceptedMints = useAcceptedMints();
  const rejectedMints = useRejectedMints();
  const executedMints = useExecutedMints();
  const failedMints = useFailedMints();
  const acceptMintRequest = useAcceptMintRequest();
  const rejectMintRequest = useRejectMintRequest();
  const cancelMintRequest = useCancelMintRequest();

  const { showBoundary } = useErrorBoundary<Error>();
  const acceptMint = useCallback(
    (r: Row) => {
      if (!registrarService.data) {
        showBoundary(new Error(ERR_ACC_MINT, { cause: 'Registrar service is not available.' }));
        return Promise.resolve();
      }

      // Get registrar service
      const registrarServiceCid = registrarService.data.contractId;

      return acceptMintRequest({
        cid: registrarServiceCid,
        argument: { cid: r.contractId as ContractId<MintRequest>, payload: {} },
      });
    },
    [registrarService, acceptMintRequest, showBoundary],
  );

  const rejectMint = useCallback(
    (r: Row, reason: string) => {
      if (!registrarService.data) {
        showBoundary(new Error(ERR_REJ_MINT, { cause: 'Registrar service is not available.' }));
        return Promise.resolve();
      }

      return rejectMintRequest({
        cid: registrarService.data.contractId,
        argument: { cid: r.contractId as ContractId<MintRequest>, payload: { reason } },
      });
    },
    [registrarService, rejectMintRequest, showBoundary],
  );

  const cancelMint = useCallback(
    async (r: Row) => {
      await cancelMintRequest({ cid: r.contractId as ContractId<MintRequest>, argument: {} });
    },
    [cancelMintRequest],
  );
  const { setDialogName, dialogName, onCloseDialog } = useDialogControl();
  const isLoading =
    registrarService.isLoading ||
    registrarServices.isLoading ||
    mintRequests.isLoading ||
    mintOffers.isLoading ||
    acceptedMints.isLoading ||
    rejectedMints.isLoading ||
    executedMints.isLoading ||
    failedMints.isLoading ||
    !mintRequests.data ||
    !mintOffers.data ||
    !acceptedMints.data ||
    !rejectedMints.data ||
    !executedMints.data ||
    !failedMints.data;
  if (isLoading) return <Loading />;

  const rows = mintRequests.data
    .map((r) => ({
      contractId: r.contractId as string,
      state: 'Requested',
      payload: r.payload.mint,
    }))
    .concat(
      mintOffers.data.map((r) => ({
        contractId: r.contractId as string,
        state: 'Offered',
        payload: r.payload.mint,
      })),
    )
    .concat(
      acceptedMints.data.map((r) => ({
        contractId: r.contractId as string,
        state: 'Accepted',
        payload: r.payload.mint,
      })),
    )
    .concat(
      rejectedMints.data.map((r) => ({
        contractId: r.contractId as string,
        state: 'Rejected',
        payload: r.payload.mint,
        reason: r.payload.reason,
      })),
    )
    .concat(
      executedMints.data.map((r) => ({
        contractId: r.contractId as string,
        state: 'Executed',
        payload: r.payload.mint,
      })),
    )
    .concat(
      failedMints.data.map((r) => ({
        contractId: r.contractId as string,
        state: `Failed`,
        payload: r.payload.mint,
        reason: r.payload.reason,
      })),
    );
  const isRegistrar = !!registrarService.data;
  const createMintActions = (r: Row) => {
    const actions: Action[] = [];
    if (isRegistrar && r.state === 'Requested' && r.payload.registrar === party)
      actions.push(
        { name: 'Accept', action: (row: Row) => Promise.resolve(acceptMint(row)) },
        {
          name: 'Reject',
          action: () => {
            setOnReject(() => (reason: string) => rejectMint(r, reason));
            return Promise.resolve();
          },
        },
      );
    if (r.state === 'Requested' && r.payload.holder === party)
      actions.push({ name: 'Cancel', action: cancelMint });
    return actions;
  };
  const buttons = [
    {
      name: 'Request mint',
      onClick: () => setDialogName('mint'),
    },
  ];
  return (
    <PageWrapper buttons={buttons} pageName="Mints">
      {!!onReject && (
        <RejectDialog
          open={!!onReject}
          onClose={() => setOnReject(undefined)}
          onReject={onReject}
        />
      )}
      {dialogName === 'mint' && <RequestMintDialog open onClose={onCloseDialog} />}
      <DataTable
        columns={createHeaders(createMintActions)}
        rows={rows}
        rowKey={(a) => a.contractId}
      />
    </PageWrapper>
  );
};
